import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import api from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1522158637959-30385a09e0da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHx3b3JzaGlwJTIwY29uY2VydCUyMHlvdXRoJTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3ODIwMDU5MTF8MA&ixlib=rb-4.1.0&q=85";

function IconFor({ name, className }) {
  const I = Icons[name] || Icons.Flame;
  return <I className={className} />;
}

export default function Ministries() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/ministries").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="ministries-page" className="bg-[#050505] text-white">
      <section className="relative py-32 md:py-40 px-6 md:px-12 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/60 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-4">Find Your Tribe</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9]">
            Ministries & <span className="text-[#ff5c00]">Departments</span>
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-8">
            You weren't built to sit. Every gift matters. Find a team, plug in
            and let's build something that outlives us.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              data-testid={`ministry-${i}`}
            >
              <Link
                to={`/ministries/${m.slug}`}
                className="group relative overflow-hidden rounded-sm border border-white/10 bg-[#0f0f14] hover:border-[#ff5c00]/60 hover:-translate-y-1 transition-all min-h-[320px] block"
              >
                {m.hero_image && (
                  <>
                    <img
                      src={m.hero_image}
                      alt={m.name}
                      className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity"
                      style={{
                        objectPosition: m.hero_image_position || "center",
                        transform: `scale(${m.hero_image_scale || 1})`,
                        transformOrigin: m.hero_image_position || "center",
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/40" />
                  </>
                )}
                <div className="relative p-7 h-full flex flex-col justify-end">
                  <IconFor name={m.icon} className="w-7 h-7 text-[#ff5c00] mb-5" />
                  <h3 className="font-display text-2xl uppercase tracking-tight mb-2">{m.name}</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed line-clamp-3">{m.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-[#ff5c00]">
                    Explore <Icons.ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 md:px-12 bg-[#080808] border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">Ready to serve?</h2>
          <p className="text-zinc-300">Tell us where you'd love to plug in — we'll connect you with the team lead.</p>
          <Link to="/contact" data-testid="ministries-cta" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-all hover:-translate-y-1">
            Join a Team
          </Link>
        </div>
      </section>
    </div>
  );
}
